import { z } from "zod";

// Mirrors supabase/functions/_shared/waitlistSignupHandler.ts. The server is the
// enforcement boundary; this schema exists so the visitor sees the problem
// before the round trip. Keep the limits and the role values in step.

export const WAITLIST_ROLES = ["agent", "team_leader", "broker_owner"] as const;
export type WaitlistRole = (typeof WAITLIST_ROLES)[number];

// Visible labels only; the submitted values are unchanged (agent / team_leader
// / broker_owner), so the waitlist-signup contract and stored rows are the same.
export const WAITLIST_ROLE_OPTIONS: { value: WaitlistRole; label: string }[] = [
  { value: "agent", label: "Agent" },
  { value: "team_leader", label: "Team" },
  { value: "broker_owner", label: "Brokerage" },
];

export type TeamFieldMode = "hidden" | "optional" | "required";

export function teamFieldMode(role: WaitlistRole | undefined): TeamFieldMode {
  if (role === "agent") return "optional";
  if (role === "team_leader") return "required";
  return "hidden";
}

export const waitlistSchema = z
  .object({
    fullName: z.string().trim().min(1, "Enter your name.").max(120, "Keep this under 120 characters."),
    email: z
      .string()
      .trim()
      .max(254, "That email is too long.")
      .regex(/^[^\s@,;]+@[^\s@,;]+\.[^\s@,;]+$/, "Enter a valid email address."),
    role: z.enum(WAITLIST_ROLES, { required_error: "Choose the option that fits you." }),
    brokerage: z.string().trim().min(1, "Enter your brokerage.").max(160, "Keep this under 160 characters."),
    teamName: z.string().trim().max(160, "Keep this under 160 characters."),
    /** Honeypot. Humans never see it; the server discards the signup if filled. */
    hp_ref: z.string(),
  })
  .superRefine((value, ctx) => {
    if (value.role === "team_leader" && !value.teamName) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["teamName"], message: "Enter your team name." });
    }
  });

export type WaitlistFormValues = z.infer<typeof waitlistSchema>;
